import { Copy } from "@phosphor-icons/react";
import React from "react";
import { GOOGLE_GUIDE_STEPS_IMAGES_MAPPER } from "../../../shared/constants";
import useFancybox from "../../../hooks/useFancyBox";

const RedirectLink = ({ href, text }: { href: string; text: string }) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 dark:text-blue-400 hover:underline"
    >
      {text}
    </a>
  );
};

const StepImage = ({
  step,
  className,
}: {
  step: keyof typeof GOOGLE_GUIDE_STEPS_IMAGES_MAPPER;
  className?: string;
}) => {
  return (
    <div className="w-max">
      <img
        src={GOOGLE_GUIDE_STEPS_IMAGES_MAPPER[step].link}
        alt="Google Play Store"
        data-fancybox="google-guide"
        data-src={GOOGLE_GUIDE_STEPS_IMAGES_MAPPER[step].link}
        className={`max-w-[900px] w-full h-auto border border-gray-300 cursor-zoom-in rounded-lg my-2 ${className}`}
      />
    </div>
  );
};

const GoogleGuide: React.FC = () => {
  const [fancyboxRef] = useFancybox({});
  return (
    <section
      id="google"
      className="scroll-mt-24 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm"
      ref={fancyboxRef}
    >
      <div className="flex items-center gap-2 mb-2">
        <svg
          width="30"
          height="30"
          viewBox="0 0 48 48"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="#FFC107"
            d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
          />
          <path
            fill="#FF3D00"
            d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
          />
          <path
            fill="#4CAF50"
            d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
          />
          <path
            fill="#1976D2"
            d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
          />
        </svg>
        <h2 className="text-3xl font-semibold text-gray-900 dark:text-white">
          Google IAP Integration
        </h2>
      </div>

      <h3 className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">
        Integrating Google Play Store with AppRevBooster
      </h3>

      <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
        This guide provides the complete, step-by-step process for integrating
        your Google Play Store account with AppRevBooster. Integrating your
        store ensures all your in-app purchase and subscription data is
        accurately consolidated for unified reporting and optimization.
      </p>

      <h3 className="mt-5 font-semibold text-gray-900 dark:text-white">
        Before You Begin (Prerequisites)
      </h3>
      <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
        Please ensure the following preliminary steps are completed:
      </p>
      <ol className="mt-2 list-decimal pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
        <li>You have a Google Cloud project associated with your app.</li>
        <li>
          Your initial setup and integration of your{" "}
          <span className="font-semibold">Google Ads</span> and{" "}
          <span className="font-semibold">Google AdMob</span> accounts with
          AppRevBooster are complete.
        </li>
        <li>
          You will need access to both the{" "}
          <span className="font-semibold">Google Cloud Console</span> and the{" "}
          <span className="font-semibold">Google Play Console</span> to complete
          the following steps.
        </li>
      </ol>

      <div className="mt-5">
        <h4 className="font-medium text-gray-900 dark:text-white">
          Step 1: Enable Developer APIs in Google Cloud Console
        </h4>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Activating the correct Developer APIs is essential for AppRevBooster
          to communicate seamlessly with your app's data, facilitating real-time
          data analysis.
        </p>
        <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
          <span className="font-semibold">Note:</span> If your app is not
          distributed via the Play Store, you can skip this step.
        </p>
        <ol className="mt-2 list-decimal pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
          <li>
            Open the{" "}
            <RedirectLink
              href="https://console.cloud.google.com/"
              text="Google Cloud Console"
            />{" "}
            and sign in.
          </li>
          <li>
            In the top-left corner, select the Google Cloud Project you wish to
            use.
            <StepImage step="1" />
          </li>
          <li>
            Enable the following three APIs, one by one:
            <ul className="mt-1 pl-4 list-none space-y-1">
              <li>
                ○​{" "}
                <span className="font-semibold">
                  <RedirectLink
                    href="https://console.cloud.google.com/apis/library/androidpublisher.googleapis.com"
                    text="Google Play Android Developer API"
                  />
                </span>{" "}
                (For general app management).
                <StepImage step="2" />
              </li>
              <li>
                ○​{" "}
                <span className="font-semibold">
                  <RedirectLink
                    href="https://console.cloud.google.com/apis/library/playdeveloperreporting.googleapis.com"
                    text="Google Play Developer Reporting API"
                  />
                </span>{" "}
                (For data and reporting access).
                <StepImage step="3" />
              </li>
              <li>
                ○​{" "}
                <span className="font-semibold">
                  <RedirectLink
                    href="https://console.cloud.google.com/marketplace/product/google/pubsub.googleapis.com"
                    text="Cloud Pub/Sub API"
                  />
                </span>{" "}
                (To enable real-time developer notifications).
                <StepImage step="4" />
              </li>
            </ul>
          </li>
          <li>
            <span className="font-semibold">Verification:</span> Navigate to the
            APIs & Services page in the Google Cloud Console and confirm that
            all three listed APIs are enabled.
            <StepImage step="5" />
          </li>
        </ol>
      </div>

      <div className="mt-5">
        <h4 className="font-medium text-gray-900 dark:text-white">
          <RedirectLink
            href="https://console.cloud.google.com/iam-admin/serviceaccounts"
            text="Step 2: Create a Service Account & Assign Roles"
          />
        </h4>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          A Service Account is a special Google account used by AppRevBooster to
          securely access your data without requiring user credentials.
        </p>
        <ol className="mt-2 list-decimal pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
          <li>
            In the Google Cloud Console, navigate to{" "}
            <RedirectLink
              href="https://console.cloud.google.com/iam-admin/serviceaccounts"
              text="IAM & Admin &gt; Service accounts"
            />{" "}
            and click the Create service account button.
            <StepImage step="6" />
          </li>
          <li>Click the Create service account button.</li>
          <li>
            In the Service account details section, enter a descriptive name. We
            recommend including the "AppRevBooster Access" name to indicate the
            purpose of this account. The Service account ID will be created
            automatically.
            <StepImage step="7" />
          </li>
          <li>
            Crucially, copy and save the full{" "}
            <span className="font-semibold">Service Account Email Address</span>{" "}
            (located just below the Service Account ID). You will need this
            email in the next step.
          </li>
          <li>Click Create and continue.</li>
          <li>
            <div className="flex items-center gap-2">
              <p>
                In the Grant this service account access to project section,
                assign the following roles:{" "}
              </p>
            </div>
            <ul className="mt-1 pl-4 list-none space-y-1">
              <li>
                ○​ Select{" "}
                <span className="font-semibold">Storage Object Viewer</span>.
                (Required for read-only access to GCS buckets).
              </li>
              <li>
                ○​ Click Add another role, then select{" "}
                <span className="font-semibold">Monitoring Viewer</span>. (This
                enables monitoring of the notification queue).
              </li>
              <li>
                ○​ Select <span className="font-semibold">Pub/Sub Admin</span>.
                (This enables real-time developer notifications).
              </li>
            </ul>
          </li>
          <li>
            Click Continue, and then click the Done button without any changes.
            The Service accounts window opens.
            <StepImage step="12" />
          </li>
        </ol>
      </div>

      <div className="mt-5">
        <h4 className="font-medium text-gray-900 dark:text-white">
          Step 3: Grant Permissions in Google Play Console
        </h4>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          You must now grant the Service Account created in Step 2 the necessary
          access within the Google Play Console.
        </p>
        <ol className="mt-2 list-decimal pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
          <li>
            Open the{" "}
            <RedirectLink
              href="https://play.google.com/console/u/1/developers/6627684772173531579/users-and-permissions"
              text="Google Play Console"
            />{" "}
            and navigate to{" "}
            <RedirectLink
              href="https://play.google.com/console/u/1/developers/6627684772173531579/users-and-permissions"
              text="Users and permissions"
            />
            .
          </li>
          <li>
            Click the Invite new users button.
            <StepImage step="14" />
          </li>
          <li>
            Paste the Service Account Email Address you copied at point no 4 in
            Step 2 into the Email address field.
            <StepImage step="15" />
          </li>
          <li>Click on the Account permissions tab.</li>
          <li>
            Grant the following permissions by checking the boxes in the
            relevant sections:
            <ul className="mt-1 pl-4 list-none space-y-1">
              <li>
                ○​{" "}
                <span className="font-semibold">
                  View app information and download bulk reports
                </span>{" "}
                (Read-only).
              </li>
              <li>
                ○​{" "}
                <span className="font-semibold">
                  View financial data, orders, and cancellation survey responses
                </span>
                .
                <StepImage step="15-2" />
              </li>
              <li>
                ○​{" "}
                <span className="font-semibold">
                  Manage orders and subscriptions
                </span>
                .
              </li>
              <li>
                ○​ <span className="font-semibold">Manage store presence</span>.
              </li>
            </ul>
          </li>
          <li>
            Click the Invite user button, and then click Send invite. The
            service account will now appear in your user list.
            <StepImage step="16" />
          </li>
        </ol>
      </div>

      <div className="mt-5">
        <h4 className="font-medium text-gray-900 dark:text-white">
          Step 4: Generating and Saving the Service Account Key (JSON)
        </h4>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Integration with AppRevBooster for your Play Store mobile app requires
          the generation of service account key files within the Google Play
          Console. These specialized files must then be uploaded to
          AppRevBooster to establish a secure link and prevent unauthorized
          access.
        </p>
        <div className="mt-2 rounded-md bg-green-50 dark:bg-green-900/30 p-3 border border-green-200 dark:border-green-800 max-w-[900px]">
          <p className="text-sm text-green-800 dark:text-green-300 font-medium">
            ⏱️ Pro-Tip: Expediting Service Account Activation
          </p>
          <p className="mt-1 text-sm text-green-700 dark:text-green-400">
            It usually takes at least 24 hours for your new service account to
            become active. However, there's a hack to speed this up. After
            creating the service account in the Google Play Console, open any
            application and navigate to Monetize → Products →
            Subscriptions/In-app products. Edit the description of any product
            and save the changes. This action should activate the service
            account immediately, and you can revert the changes afterward.
          </p>
        </div>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          This key file will be uploaded to AppRevBooster to secure the
          connection.
        </p>

        <ol className="mt-2 list-decimal pl-6 text-md text-gray-800 dark:text-gray-200 space-y-1">
          <li>
            <RedirectLink
              href="https://console.cloud.google.com/iam-admin/serviceaccounts"
              text="Open Service Accounts & Start Key Creation"
            />
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Navigate to the Service accounts section in the Google Play
              Console. Important: Verify that you have selected the correct
              project. Once confirmed, click Add key and then select Create new
              key from the dropdown menu.
            </p>
            <StepImage step="17" />
          </li>
          <li>
            Generate and Download the Key File
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              In the confirmation window ("Create private key for
              [Your_project_name]"), click the Create button. Your browser will
              automatically download the private key file.
            </p>
            <StepImage step="18" />
          </li>
          <li>
            Locate and Secure the Key File
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              The key is saved as a JSON file (e.g.,
              [project-name]-[unique-id].json) on your computer. You can
              reference the file name provided in the final confirmation window
              ("Private key created for [Your_project_name]").
            </p>
            <StepImage step="19" />
          </li>
        </ol>
      </div>

      <div className="mt-5">
        <h4 className="font-medium text-gray-900 dark:text-white">
          <RedirectLink
            href="https://play.google.com/console/u/1/developers"
            text="Step 5: Get the Financial Reports Bucket Name"
          />
        </h4>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          You need the <strong>Cloud Storage URI (Bucket name)</strong> where
          your financial reports are stored to enable AppRevBooster to fetch
          historical revenue data.
        </p>
        <ol className="mt-2 list-decimal pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
          <li>
            In the Google Play Console, scroll down the left navigation bar to
            the{" "}
            <RedirectLink
              href="https://play.google.com/console/u/1/developers"
              text="Download Reports"
            />{" "}
            section.
          </li>
          <li>Click on Financial.</li>
          <li>
            In the Download Financial Reports section on the right, look for the
            Cloud Storage URI link.
          </li>
          <li>
            <div className="flex items-center gap-1">
              Click <Copy />
              <span className="font-semibold">Copy Cloud Storage URI.</span>
              This is your <span className="font-semibold">Bucket Name.</span>
            </div>
            <StepImage step="21" />
          </li>
        </ol>
      </div>

      <div className="mt-5">
        <h4 className="font-medium text-gray-900 dark:text-white">
          Step 6: Complete Integration in AppRevBooster
        </h4>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          You now have the two key items needed to finish the integration:
        </p>
        <ul className="mt-2 list-disc pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
          <li>
            The{" "}
            <span className="font-semibold">Service Account JSON Key File</span>{" "}
            (from Step 4).
          </li>
          <li>
            The <span className="font-semibold">Bucket Name</span> (Cloud
            Storage URI from Step 5).
          </li>
        </ul>
        <ol className="mt-2 list-decimal pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
          <li>Log in to your AppRevBooster account.</li>
          <li>
            Navigate to the integration or Add Account section for Google IAP.
          </li>
          <li>
            Upload the <span className="font-semibold">JSON Key File</span>.
          </li>
          <li>
            Paste the copied <span className="font-semibold">Bucket Name</span>{" "}
            into the corresponding field.
          </li>
          <li>Complete the final steps in the AppRevBooster interface.</li>
        </ol>
        <StepImage step="22" className="!w-auto h-[500px]" />
        <div className="mt-3 rounded-md bg-green-50 dark:bg-green-900/30 p-3 border border-green-200 dark:border-green-800 max-w-[900px]">
          <p className="text-sm text-green-800 dark:text-green-300 font-medium">
            Integration Complete!
          </p>
          <p className="mt-1 text-sm text-green-700 dark:text-green-400">
            Your Google Play Store account is now integrated with AppRevBooster.
            You can begin managing and analyzing your in-app purchase and
            subscription data seamlessly from your dashboard.
          </p>
        </div>
      </div>
    </section>
  );
};

export default GoogleGuide;
